import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {summarizeConversation} from '../../../../../libs/shared/core/src/helpers/ai/conversationSummaryHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {HttpResponseError} from '../../../../../libs/shared/core/src/services/http/httpError';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {DialogButtonGroup} from '../../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {helpCenterUrl, navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {DraftAssistantLoader} from '../../composer/toolbar/dropdowns/draftAssistantLoader';
import {CommentComposerIcon} from './commentComposerIcon';

/*
 * Props.
 */

interface CommentComposerSummarizeProps {
  conversationId: number;
  conversationHasMessages: boolean;
  isDisabled?: boolean;
  onInsertSummarizeText: (arg: string) => void;
  companyUrl: string;
}

/*
 * Constants.
 */

const dropdownWidth = 300;

/*
 * Styles.
 */

const StyledTextDiv = styled.div`
  padding: 18px 20px;
  line-height: 22px;
`;

const StyledLoaderContainerDiv = styled.div`
  height: 132px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledDialogContentDiv = styled.div`
  padding: 18px 20px 0px 20px;
`;

const StyledDescriptionDiv = styled.div`
  padding-bottom: 20px;
  line-height: 22px;
`;

const StyledIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const CommentComposerSummarize: FC<CommentComposerSummarizeProps> = (props) => {
  const dispatch = useAppDispatch();

  const {companyUrl, conversationId, isDisabled, onInsertSummarizeText} = props;

  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADED);
  const [error, setError] = useState<HttpResponseError | undefined>(undefined);

  const onClickSave = async (onRequestClose: () => void) => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);

    const summary = await summarizeConversation({
      conversationId,
      companyUrl,
    }).catch((err) => {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      if (err instanceof HttpResponseError) {
        setError(err);
      }
    });

    // Something went wrong - set loading state to failed so user can retry.
    if (!summary) {
      setLoadingStatus(AsyncStatusesEnum.FAILED);
      return;
    }

    onInsertSummarizeText(summary);
    setLoadingStatus(AsyncStatusesEnum.LOADED);
    setError(undefined);

    trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_AI_SUMMARIZE_INSERT, {
      conversation_id: conversationId,
    });

    if (onRequestClose) {
      onRequestClose();
    }
  };

  const renderDropdownContent = (popoverProps: PopoverTransferProps) => {
    if (!props.conversationHasMessages) {
      return (
        <StyledTextDiv>
          <Trans id="comment-composer-summarize-no-messages">
            Summarization is only available for conversations with messages.
          </Trans>
        </StyledTextDiv>
      );
    }

    // Loading and error states
    if (loadingStatus === AsyncStatusesEnum.LOADING || loadingStatus === AsyncStatusesEnum.FAILED) {
      return (
        <StyledLoaderContainerDiv>
          <DraftAssistantLoader
            error={error}
            loadingStatus={loadingStatus}
            loadingMessage={
              <Trans id="draft-assistant-autocompose-async-loading">Generating conversation summary...</Trans>
            }
            onRetryClick={() => onClickSave(popoverProps.onRequestClose)}
          />
        </StyledLoaderContainerDiv>
      );
    }

    return (
      <>
        <StyledDialogContentDiv>
          <StyledDescriptionDiv>
            <Trans id="comment-composer-summarize-tip">
              A short summary of the messages in this conversation will be generated.
            </Trans>
          </StyledDescriptionDiv>
        </StyledDialogContentDiv>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <Button onClick={() => dispatch(navigateToLinkAction(`${helpCenterUrl}/t/60hc3w8`))}>
            <StyledButtonContent>
              <Trans id="comment-composer-summarize-dropdown-learn-more">Learn more</Trans>
              <StyledIcon name="externalLinkSmall" />
            </StyledButtonContent>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => onClickSave(popoverProps.onRequestClose)}
          >
            <Trans id="comment-composer-generate-summary">Generate</Trans>
          </Button>
        </DialogButtonGroup>
      </>
    );
  };

  const renderButton: RendererOf<boolean> = (isOpen) => (
    <CommentComposerIcon
      iconName="magicWandSmall"
      isDisabled={isDisabled}
      hasHover={isOpen}
      renderTooltip={() =>
        isDisabled ? (
          <Trans id="comment-composer-ai-summarize-tooltip-disabled">AI feature disabled</Trans>
        ) : (
          <Trans id="comment-composer-ai-summarize-tooltip-enabled">Summarize with AI</Trans>
        )
      }
    />
  );

  const renderDropdown: RendererOf<PopoverTransferProps> = (popoverProps) => (
    <Dropdown {...popoverProps} width={dropdownWidth} placement="top-end">
      <DropdownHeader title={<Trans id="draft-assistant-magiccompose">Summarize with AI</Trans>} />
      {renderDropdownContent(popoverProps)}
    </Dropdown>
  );

  return (
    <PopoverCoordinator
      shouldPreventMouseDown
      isDisabled={isDisabled}
      renderButton={renderButton}
      renderModal={renderDropdown}
      onPopoverToggle={(isOpen) => {
        if (isOpen) {
          trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_AI_SUMMARIZE_CLICK, {
            conversation_id: conversationId,
          });
        }
      }}
    />
  );
};
